import { ReactNode, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import AppDataContext, { AppDataContextValue } from '../../contexts/appData';
import { useIotsApi } from '../../contexts/iotsApi';
import useQueryErrorNotifier from '../../hooks/useQueryErrorNotifier';
import { Port, PortSelectItem } from '../../types/port';
import { useLanguage } from '../../contexts/language';

const AppDataProvider = ({
  children,
  loadingContent,
}: {
  children: ReactNode;
  loadingContent: ReactNode;
}) => {
  const { getAppData, getIslands } = useIotsApi();
  const { language } = useLanguage();

  const {
    data: appData = null,
    isFetching: isFetchingAppData,
    error: appDataError,
  } = useQuery({
    queryKey: ['appData'],
    queryFn: () => getAppData(),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  const {
    data: islands = [],
    error: islandsError,
    isLoading: isLoadingIslands,
    isFetching: isFetchingIslands,
    refetch: refetchIslands,
  } = useQuery({
    queryKey: ['islands'],
    queryFn: () => getIslands(),
    refetchOnMount: 'always',
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });

  useQueryErrorNotifier([appDataError, islandsError]);

  const { ports, portsMap } = useMemo(() => {
    const ports: PortSelectItem[] = [];
    const portsMap: Record<string, PortSelectItem> = {};

    if (isLoadingIslands) {
      return {
        ports,
        portsMap,
      };
    }

    islands.forEach(island => {
      const islandName = island.names[language];

      island.ports.forEach(islandPort => {
        const port: Port = {
          ...islandPort,
          islandId: island.id,
          islandNames: island.names,
        };

        const portName = port.names[language];

        const selectItem: PortSelectItem = {
          value: port.id,
          label:
            portName !== islandName
              ? [islandName, portName].join(' - ')
              : portName,
          issue: !port.isActive ? 'inactive' : null,
          portInstance: port,
        };

        portsMap[selectItem.value] = selectItem;
        ports.push(selectItem);
      });

      ports.sort((a, b) => a.label.localeCompare(b.label));
    });

    return {
      ports,
      portsMap,
    };
  }, [isLoadingIslands, islands, language]);

  const contextValue = useMemo<AppDataContextValue | null>(() => {
    if (!appData || isLoadingIslands) {
      return null;
    }

    return {
      appData,
      islands,
      ports,
      portsMap,
      isFetching: isFetchingAppData || isFetchingIslands,
      refetchIslandAndPortData: () => void refetchIslands(),
    };
  }, [
    appData,
    isLoadingIslands,
    islands,
    ports,
    portsMap,
    isFetchingAppData,
    isFetchingIslands,
    refetchIslands,
  ]);

  if ((!appData && appDataError) || (!isLoadingIslands && islandsError)) {
    throw new Error('The application data could not be retrieved');
  }

  if (!contextValue) {
    return loadingContent;
  }

  return (
    <AppDataContext.Provider value={contextValue}>
      {children}
    </AppDataContext.Provider>
  );
};

export default AppDataProvider;
